<template>
  <nav
    v-if="loading || (journey && journey.items.length)"
    :class="[$style.navigator, layout === 'drawer' && $style.drawerLayout]"
    :aria-label="localizedStrings['content-pathing'] || 'Content Pathing'"
    :aria-busy="loading || undefined"
  >
    <div v-if="loading" :class="$style.loadingPanel">
      <span :class="$style.loadingHeading" />
      <span v-for="index in 4" :key="index" :class="$style.loadingItem" />
    </div>

    <template v-else-if="journey">
      <div :class="$style.desktopHeader">
        <button
          :class="$style.desktopToggle"
          type="button"
          :aria-label="
            desktopOpen
              ? localizedStrings['content-pathing-module-collapse'] ||
                'Collapse content pathing module'
              : localizedStrings['content-pathing-module-expand'] ||
                'Expand content pathing module'
          "
          :aria-expanded="desktopOpen"
          @click="desktopOpen = !desktopOpen"
        >
          <svg
            :class="$style.journeyIcon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M20 19L20.5303 19.5303C20.8232 19.2374 20.8232 18.7626 20.5303 18.4697L20 19ZM8 4.25C7.58579 4.25 7.25 4.58579 7.25 5C7.25 5.41421 7.58579 5.75 8 5.75V4.25ZM18.5303 16.4697C18.2374 16.1768 17.7626 16.1768 17.4697 16.4697C17.1768 16.7626 17.1768 17.2373 17.4697 17.5303L18.5303 16.4697ZM17.4697 20.4697C17.1768 20.7626 17.1768 21.2373 17.4697 21.5303C17.7626 21.8232 18.2374 21.8232 18.5303 21.5303L17.4697 20.4697ZM13.75 18.25C13.3358 18.25 13 18.5858 13 19C13 19.4142 13.3358 19.75 13.75 19.75V18.25ZM10 19.75C10.4142 19.75 10.75 19.4142 10.75 19C10.75 18.5858 10.4142 18.25 10 18.25V19.75ZM11 5.75C11.4142 5.75 11.75 5.4142 11.75 5C11.75 4.5858 11.4142 4.25 11 4.25V5.75ZM15 4.25C14.5858 4.25 14.25 4.5858 14.25 5C14.25 5.4142 14.5858 5.75 15 5.75V4.25ZM7.5 12.75H16.5V11.25H7.5V12.75ZM20.5303 18.4697L18.5303 16.4697L17.4697 17.5303L19.4697 19.5303L20.5303 18.4697ZM19.4697 18.4697L17.4697 20.4697L18.5303 21.5303L20.5303 19.5303L19.4697 18.4697ZM20.75 8.5C20.75 6.1528 18.8472 4.25 16.5 4.25V5.75C18.0188 5.75 19.25 6.9812 19.25 8.5H20.75ZM16.5 12.75C18.8472 12.75 20.75 10.8472 20.75 8.5H19.25C19.25 10.0188 18.0188 11.25 16.5 11.25V12.75ZM4.75 15.5C4.75 13.9812 5.9812 12.75 7.5 12.75V11.25C5.1528 11.25 3.25 13.1528 3.25 15.5H4.75ZM7.5 18.25C5.9812 18.25 4.75 17.0188 4.75 15.5H3.25C3.25 17.8472 5.1528 19.75 7.5 19.75V18.25ZM20 18.25H13.75V19.75H20V18.25ZM10 18.25H7.5V19.75H10V18.25ZM11 4.25H8V5.75H11V4.25ZM16.5 4.25H15V5.75H16.5V4.25Z"
              fill="currentColor"
            />
            <circle
              cx="6"
              cy="5"
              r="2"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
          <span :class="$style.titleText">{{ journey.title }}</span>
          <svg
            :class="[$style.arrow, desktopOpen && $style.arrowOpen]"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <p
          v-if="desktopOpen && journey.description"
          :class="$style.description"
        >
          {{ journey.description }}
        </p>
      </div>

      <div v-if="desktopOpen" :class="$style.desktopOnly">
        <ol :class="$style.items">
          <li v-for="(item, index) in journey.items" :key="item.id">
            <NuxtLinkLocale
              :to="withContentJourneyQuery(item.route, journey.id)"
              :aria-current="item.isCurrent ? 'page' : undefined"
              :class="[item.isCurrent && $style.current]"
            >
              <span v-if="journey.displayStepLabels" :class="$style.stepLabel">
                {{ index + 1 }}.
              </span>
              <span>{{ item.label }}</span>
            </NuxtLinkLocale>
          </li>
        </ol>
      </div>

      <button
        :class="[$style.mobileToggle, mobileOpen && $style.mobileToggleOpen]"
        type="button"
        aria-haspopup="true"
        :aria-label="
          mobileOpen
            ? localizedStrings['content-pathing-module-close'] ||
              'Close content pathing module'
            : localizedStrings['content-pathing-module-open'] ||
              'Open content pathing module'
        "
        :aria-expanded="mobileOpen"
        @click="mobileOpen = !mobileOpen"
      >
        <svg
          :class="$style.journeyIcon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M20 19L20.5303 19.5303C20.8232 19.2374 20.8232 18.7626 20.5303 18.4697L20 19ZM8 4.25C7.58579 4.25 7.25 4.5858 7.25 5C7.25 5.4142 7.58579 5.75 8 5.75V4.25ZM18.5303 16.4697C18.2374 16.1768 17.7626 16.1768 17.4697 16.4697C17.1768 16.7626 17.1768 17.2373 17.4697 17.5303L18.5303 16.4697ZM17.4697 20.4697C17.1768 20.7626 17.1768 21.2373 17.4697 21.5303C17.7626 21.8232 18.2374 21.8232 18.5303 21.5303L17.4697 20.4697ZM13.75 18.25C13.3358 18.25 13 18.5858 13 19C13 19.4142 13.3358 19.75 13.75 19.75V18.25ZM10 19.75C10.4142 19.75 10.75 19.4142 10.75 19C10.75 18.5858 10.4142 18.25 10 18.25V19.75ZM11 5.75C11.4142 5.75 11.75 5.4142 11.75 5C11.75 4.5858 11.4142 4.25 11 4.25V5.75ZM15 4.25C14.5858 4.25 14.25 4.5858 14.25 5C14.25 5.4142 14.5858 5.75 15 5.75V4.25ZM7.5 12.75H16.5V11.25H7.5V12.75ZM20.5303 18.4697L18.5303 16.4697L17.4697 17.5303L19.4697 19.5303L20.5303 18.4697ZM19.4697 18.4697L17.4697 20.4697L18.5303 21.5303L20.5303 19.5303L19.4697 18.4697ZM20.75 8.5C20.75 6.1528 18.8472 4.25 16.5 4.25V5.75C18.0188 5.75 19.25 6.9812 19.25 8.5H20.75ZM16.5 12.75C18.8472 12.75 20.75 10.8472 20.75 8.5H19.25C19.25 10.0188 18.0188 11.25 16.5 11.25V12.75ZM4.75 15.5C4.75 13.9812 5.9812 12.75 7.5 12.75V11.25C5.1528 11.25 3.25 13.1528 3.25 15.5H4.75ZM7.5 18.25C5.9812 18.25 4.75 17.0188 4.75 15.5H3.25C3.25 17.8472 5.1528 19.75 7.5 19.75V18.25ZM20 18.25H13.75V19.75H20V18.25ZM10 18.25H7.5V19.75H10V18.25ZM11 4.25H8V5.75H11V4.25ZM16.5 4.25H15V5.75H16.5V4.25Z"
            fill="currentColor"
          />
          <circle
            cx="6"
            cy="5"
            r="2"
            stroke="currentColor"
            stroke-width="1.5"
          />
        </svg>
        <span :class="$style.titleText">{{ journey.title }}</span>
        <svg
          :class="[$style.arrow, mobileOpen && $style.arrowOpen]"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div v-if="mobileOpen" :class="$style.mobileDropdown" role="region">
        <p v-if="journey.description" :class="$style.mobileDescription">
          {{ journey.description }}
        </p>
        <ol :class="$style.items">
          <li v-for="(item, index) in journey.items" :key="item.id">
            <NuxtLinkLocale
              :to="withContentJourneyQuery(item.route, journey.id)"
              :aria-current="item.isCurrent ? 'page' : undefined"
              :class="[item.isCurrent && $style.current]"
            >
              <span v-if="journey.displayStepLabels" :class="$style.stepLabel">
                {{ index + 1 }}.
              </span>
              <span>{{ item.label }}</span>
            </NuxtLinkLocale>
          </li>
        </ol>
      </div>
    </template>
  </nav>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  type ContentJourneyData,
  withContentJourneyQuery,
} from '~/services/contentJourney'

const { localizedStrings } = useLocalizedStrings()

const props = withDefaults(
  defineProps<{
    journey?: ContentJourneyData | null
    loading?: boolean
    initiallyExpanded?: boolean
    mobileInitiallyExpanded?: boolean
    layout?: 'responsive' | 'drawer'
  }>(),
  {
    journey: null,
    loading: false,
    initiallyExpanded: true,
    mobileInitiallyExpanded: false,
    layout: 'responsive',
  },
)

const desktopOpen = ref(props.initiallyExpanded)
const mobileOpen = ref(props.mobileInitiallyExpanded)

watch(
  () => props.initiallyExpanded,
  (initiallyExpanded) => {
    desktopOpen.value = initiallyExpanded
  },
)

watch(
  () => props.mobileInitiallyExpanded,
  (mobileInitiallyExpanded) => {
    mobileOpen.value = mobileInitiallyExpanded
  },
)
</script>

<style lang="scss" module>
.navigator {
  width: 100%;
  max-width: 54rem;
  margin: 0 auto;
  background: var(--white);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.loadingPanel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 14px 18px;
}

.loadingHeading,
.loadingItem {
  animation: journeyPulse 1.4s ease-in-out infinite;
  background: #e5e7eb;
  border-radius: 4px;
  display: block;
  height: 14px;
}

.loadingHeading {
  height: 18px;
  margin-bottom: 2px;
  width: 55%;
}

.loadingItem {
  width: 88%;
}

.loadingItem:nth-child(3) {
  animation-delay: 0.12s;
  width: 76%;
}

.loadingItem:nth-child(4) {
  animation-delay: 0.24s;
  width: 84%;
}

.loadingItem:nth-child(5) {
  animation-delay: 0.36s;
  width: 68%;
}

@keyframes journeyPulse {
  0%,
  100% {
    opacity: 0.55;
  }

  50% {
    opacity: 1;
  }
}

.desktopHeader {
  display: block;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #e5e7eb;
}

.desktopToggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #484848;
  cursor: pointer;
  display: flex;
  padding: 0;
  text-align: left;
  width: 100%;
}

.journeyIcon {
  color: #635fa6;
  flex: 0 0 16px;
  margin-right: 8px;
}

.desktopToggle .arrow {
  flex: 0 0 auto;
  margin-left: auto;
  transition: transform 0.2s ease;
}

.arrowOpen {
  transform: rotate(90deg);
}

.titleText {
  color: #484848;
  font-family: var(--roboto-condensed);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.description,
.mobileDescription {
  color: #626262;
  font-family: var(--base-font-family);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  margin: 8px 0 0;
}

.description {
  margin-left: 24px;
}

.desktopOnly {
  display: block;
}

.items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 12px 14px 14px;
}

.items li {
  line-height: 1.3;
}

.items a {
  color: #484848;
  display: inline-flex;
  font-family: var(--roboto-condensed);
  font-size: 15px;
  gap: 5px;
  text-decoration: none;
}

.items a:hover,
.items a:focus-visible,
.current {
  color: #635fa6 !important;
}

.stepLabel {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
}

.mobileToggle,
.mobileDropdown {
  display: none;
}

.drawerLayout .desktopHeader,
.drawerLayout .desktopOnly {
  display: block;
}

.navigator.drawerLayout {
  background: var(--white);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.drawerLayout .mobileToggle,
.drawerLayout .mobileDropdown {
  display: none;
}

@include media('less-than-lg') {
  .desktopHeader,
  .desktopOnly {
    display: none;
  }

  .navigator {
    position: relative;
    overflow: visible;
    border: 0;
    background: transparent;
  }

  .mobileToggle {
    align-items: center;
    background: var(--white);
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #484848;
    cursor: pointer;
    display: inline-flex;
    gap: 10px;
    justify-content: flex-start;
    padding: 14px 18px;
    text-align: left;
    width: 100%;
  }

  .mobileToggle .journeyIcon {
    margin-right: 0;
  }

  .mobileToggleOpen {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-color: transparent;
  }

  .arrow {
    flex: 0 0 auto;
    height: 16px;
    margin-left: auto;
    transition: transform 0.2s ease;
    width: 16px;
  }

  .arrowOpen {
    transform: rotate(90deg);
  }

  .mobileDropdown {
    background: var(--white);
    border: 1px solid #e5e7eb;
    border-radius: 0 0 8px 8px;
    display: block;
    left: 0;
    position: absolute;
    right: 0;
    top: calc(100% - 1px);
    z-index: 2;
  }

  .mobileDescription {
    padding: 12px 18px 0 42px;
  }

  .mobileDropdown .items {
    padding-top: 12px;
  }
}
</style>
